import { ProjectProps } from "./projectDetails";
import Link from "next/link";
import Image from "next/image";
import AnimatedTitle from "../../animations/AnimatedTitle";
import AnimatedBody from "../../animations/AnimatedBody";
import { motion } from "framer-motion";
import Container from "../container/Container";
import React from "react";
import {BsLink45Deg} from "react-icons/bs";
const ProjectCard = ({
    id,
    name,
    description,
    technologies,
    techNames,
    techLinks,
    github,
    demo,
    image,
    available,
}: ProjectProps) => {
    const isEven = id % 2 === 0;

    return (
        <motion.div
            className="relative z-10 w-full"
            initial="initial"
            animate="animate"
        >
            <Container
                width="100%"
                height="100%"
                borderRadius={25}
                color="rgba(255, 255, 255, 0.1)"
                blur={false}
                grain={true}
                top="0px"
                left="0px"
                angle={0}
            >
                {/* Mobile: Stack layout / Desktop: Side by side with overlap */}
                <div className="flex flex-col lg:relative lg:h-[500px]">
                    {/* Content section */}
                    <div className={`relative z-20 p-6 pb-4 md:p-8 lg:absolute lg:top-10 lg:max-w-[50%] ${
                        isEven ? "lg:left-4" : "lg:right-4 lg:text-left"
                    }`}>
                        {/* Title with link */}
                        {demo ? (
                            <Link
                                href={demo}
                                target="_blank"
                                className="group inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
                            >
                                <AnimatedTitle
                                    text={name}
                                    className="text-[28px] leading-tight text-white sm:text-[32px] md:text-[40px] lg:text-[48px] lg:leading-none"
                                    wordSpace={"mr-[0.25em]"}
                                    charSpace={"-mr-[0.01em]"}
                                />
                                <BsLink45Deg className="text-[16px] text-white sm:text-[18px] md:text-[20px] lg:text-[24px] group-hover:translate-x-1 transition-transform" />
                            </Link>
                        ) : (
                            <AnimatedTitle
                                text={name}
                                className="text-[28px] leading-tight text-white sm:text-[32px] md:text-[40px] lg:text-[48px] lg:leading-none"
                                wordSpace={"mr-[0.25em]"}
                                charSpace={"-mr-[0.01em]"}
                            />
                        )}

                        {/* Description */}
                        <AnimatedBody
                            text={description}
                            className="mt-3 text-[14px] font-semibold text-[#95979D] sm:text-[15px] md:mt-4 md:text-[16px] lg:max-w-[400px]"
                        />

                        {/* Technologies */}
                        <div className="mt-5 mb-2 flex flex-wrap gap-4 md:mt-6 md:gap-5 lg:mt-8">
                            {technologies.map((IconComponent, idx) => (
                                <div key={idx} className="relative">
                                    <Link
                                        href={techLinks[idx]}
                                        target="_blank"
                                        aria-label={`Learn more about ${techNames[idx]}`}
                                        className="block text-[18px] text-white md:text-[22px] lg:text-[26px]"
                                        title={techLinks[idx]}
                                        data-blobity-tooltip={techNames[idx]}
                                        data-blobity-magnetic="false"
                                    >
                                        <IconComponent/>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Image section */}
                    <div className={`relative w-full px-4 pb-4 md:px-6 md:pb-6 lg:absolute lg:bottom-0 lg:w-[55%] lg:px-0 lg:pb-0 ${
                        isEven ? "lg:right-0" : "lg:left-0"
                    }`}>
                        <Image
                            src={image}
                            alt={name}
                            width={600}
                            height={400}
                            className="w-full rounded-lg object-cover"
                            priority={true}
                        />
                    </div>
                </div>
            </Container>
        </motion.div>
    );
};

export default ProjectCard;
